import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Check, Volume2, Undo2, Music } from "lucide-react";
import Modal from "@/components/chat/Modal";
import { useI18n } from "@/context/I18nContext";
import { fileUrl } from "@/lib/uploads";
import {
  RINGTONE_CATEGORIES, loadRingtones, cachedRingtones, ringSelection,
  setRingSelection, resolveTone, previewSrc, stopPreview, playTone, soundsEnabled,
} from "@/lib/sound";
import { toast } from "sonner";

const USER_CATS = ["incoming_audio", "incoming_video", "new_message", "sent_message"];
const fmtDur = (s) => { if (!s || s < 0) return ""; const m = Math.floor(s / 60); const ss = Math.round(s % 60); return `${m}:${ss.toString().padStart(2, "0")}`; };

export default function RingtoneSettingsDialog({ onClose }) {
  const { lang } = useI18n();
  const fr = lang === "fr";
  const [items, setItems] = useState(cachedRingtones());
  const [sel, setSel] = useState({ ...ringSelection() });
  const [playing, setPlaying] = useState(null); // ringtone id currently previewing
  const audioRef = useRef(null);

  useEffect(() => { loadRingtones(true).then((d) => setItems([...(d || [])])); return () => stopPreview(); }, []);

  const catLabel = (c) => (fr ? c.labelFr : c.label);
  const forCat = (key) => items.filter((r) => r.category === key);

  const stop = () => { stopPreview(); if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; } setPlaying(null); };
  const previewServer = (rt) => {
    if (playing === rt.id) { stop(); return; }
    stop();
    const a = previewSrc(fileUrl(rt.url));
    if (a) { a.onended = () => setPlaying(null); audioRef.current = a; setPlaying(rt.id); }
  };
  const previewDefault = (key) => {
    stop();
    const t = resolveTone(key);
    if (t.kind === "file") { const a = previewSrc(t.src); if (a) { audioRef.current = a; } }
    else playTone(t.id);
  };

  const select = async (key, id) => {
    const next = { ...sel };
    if (id) next[key] = id; else delete next[key];
    setSel(next);
    await setRingSelection(key, id);
  };
  const reset = async (key) => { await select(key, null); toast.success(fr ? "Réinitialisé" : "Reset to default"); };
  const test = (key) => {
    if (!soundsEnabled()) { toast(fr ? "Activez d'abord les sons dans les réglages" : "Turn on sound effects first"); return; }
    stop();
    const t = resolveTone(key);
    if (t.kind === "file") { const a = previewSrc(t.src); if (a) audioRef.current = a; }
    else playTone(t.id);
  };

  const isSelected = (key, id) => (sel[key] || null) === (id || null);

  return (
    <Modal title={fr ? "Sonneries" : "Ringtones"} onClose={() => { stop(); onClose(); }}>
      <div className="space-y-6" data-testid="ringtone-settings">
        {!soundsEnabled() && (
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-300">
            {fr ? "Les effets sonores sont désactivés. Activez-les dans Réglages pour entendre les sonneries." : "Sound effects are off. Turn them on in Settings to hear ringtones."}
          </div>
        )}
        {USER_CATS.map((key) => {
          const c = RINGTONE_CATEGORIES.find((x) => x.key === key);
          const list = forCat(key);
          const usingDefault = !sel[key] || !list.some((r) => r.id === sel[key]);
          return (
            <div key={key} data-testid={`ringtone-cat-section-${key}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold tracking-widest text-[var(--rc-muted)] uppercase flex items-center gap-2"><Music size={13} className="text-[var(--rc-gold)]" /> {catLabel(c)}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => test(key)} data-testid={`ringtone-test-${key}`} className="flex items-center gap-1 text-xs text-[var(--rc-gold)] rounded-full border border-[var(--rc-gold)]/40 px-2.5 py-1"><Volume2 size={13} /> {fr ? "Tester" : "Test Sound"}</button>
                  <button onClick={() => reset(key)} data-testid={`ringtone-reset-${key}`} className="flex items-center gap-1 text-xs text-[var(--rc-muted)] rounded-full border border-[var(--rc-border)] px-2.5 py-1"><Undo2 size={13} /> {fr ? "Défaut" : "Default"}</button>
                </div>
              </div>
              <div className="space-y-1.5">
                {/* Default (built-in) option */}
                <button onClick={() => select(key, null)} data-testid={`ringtone-option-default-${key}`}
                  className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${usingDefault ? "border-[var(--rc-gold)] bg-[var(--rc-gold)]/5" : "border-[var(--rc-border)]"}`}>
                  <span onClick={(e) => { e.stopPropagation(); previewDefault(key); }} role="button"
                    className="h-9 w-9 shrink-0 grid place-items-center rounded-full border border-[var(--rc-border)] text-[var(--rc-gold)]"><Play size={15} /></span>
                  <span className="flex-1 min-w-0 text-sm">{fr ? "Sonnerie par défaut" : "Default tone"}</span>
                  {usingDefault && <Check size={17} className="text-[var(--rc-gold)] shrink-0" />}
                </button>
                {list.map((rt) => (
                  <button key={rt.id} onClick={() => select(key, rt.id)} data-testid={`ringtone-option-${rt.id}`}
                    className={`w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${isSelected(key, rt.id) ? "border-[var(--rc-gold)] bg-[var(--rc-gold)]/5" : "border-[var(--rc-border)] hover:border-[var(--rc-gold)]/40"}`}>
                    <span onClick={(e) => { e.stopPropagation(); previewServer(rt); }} role="button" data-testid={`ringtone-preview-${rt.id}`}
                      className="h-9 w-9 shrink-0 grid place-items-center rounded-full border border-[var(--rc-border)] text-[var(--rc-gold)]">
                      {playing === rt.id ? <Pause size={15} /> : <Play size={15} />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm truncate">{rt.name}</span>
                      {rt.duration ? <span className="block text-[11px] text-[var(--rc-muted)]">{fmtDur(rt.duration)}</span> : null}
                    </span>
                    {isSelected(key, rt.id) && <Check size={17} className="text-[var(--rc-gold)] shrink-0" />}
                  </button>
                ))}
                {list.length === 0 && <p className="text-xs text-[var(--rc-muted)] px-1">{fr ? "Aucune sonnerie personnalisée — la sonnerie par défaut est utilisée." : "No custom ringtones — the default tone is used."}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
